import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const {
    cartCount
  } = useCart();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileActiveDropdown(null);
  }, [location]);
  const navItems = [{
    name: 'Events',
    href: '/events',
    isRoute: true
  }, {
    name: 'Menu',
    href: '/menu',
    isRoute: true
  }, {
    name: 'Gallery',
    href: '/gallery',
    isRoute: true
  }, {
    name: 'Contact Us',
    href: '/contact',
    isRoute: true
  }, {
    name: 'Reservations',
    href: '#reservations',
    dropdown: [{
      name: 'Brunch',
      href: '/brunch',
      isRoute: true
    }, {
      name: 'Nightlife',
      href: '/nightlife',
      isRoute: true
    }, {
      name: 'Dining',
      href: '/dining',
      isRoute: true
    }, {
      name: 'Hookah / Daytime',
      href: '/daytime',
      isRoute: true
    }]
  }];
  const isActive = (item: any) => {
    if (item.isRoute && location.pathname === item.href) return true;
    if (item.dropdown) {
      return item.dropdown.some((sub: any) => sub.isRoute && location.pathname === sub.href);
    }
    return false;
  };
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || location.pathname !== '/' ? 'bg-[#0B1014]/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`} data-id="element-474">
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between" data-id="element-475">
          {/* Logo */}
          <Link to="/" className="z-50" data-id="element-476">
            <img src="https://cdn.magicpatterns.com/uploads/kEvjjfeq9ckri24mEFMCRs/HOF-logo.png" alt="House of Flavors" className="h-20 md:h-32 w-auto transition-all duration-300" data-id="element-477" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" data-id="element-478">
            {navItems.map(item => <div key={item.name} className="relative group" onMouseEnter={() => setActiveDropdown(item.name)} onMouseLeave={() => setActiveDropdown(null)} data-id="element-479">
                {item.isRoute ? <Link to={item.href} className={`flex items-center text-lg font-medium transition-colors tracking-wide ${isActive(item) ? 'text-[#DDA247]' : 'text-white hover:text-[#DDA247]'}`} data-id="element-480">
                    {item.name}
                    {isActive(item) && <motion.div layoutId="activeNav" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#DDA247]" transition={{
                type: 'spring',
                stiffness: 380,
                damping: 30
              }} data-id="element-481" />}
                  </Link> : <a href={item.href} className={`flex items-center text-lg font-medium transition-colors tracking-wide ${isActive(item) ? 'text-[#DDA247]' : 'text-white hover:text-[#DDA247]'}`} data-id="element-482">
                    {item.name}
                    {item.dropdown && <ChevronDown className="ml-1 w-4 h-4" data-id="element-483" />}
                    {isActive(item) && <motion.div layoutId="activeNav" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#DDA247]" transition={{
                type: 'spring',
                stiffness: 380,
                damping: 30
              }} data-id="element-484" />}
                  </a>}

                {/* Dropdown */}
                {item.dropdown && <AnimatePresence data-id="element-485">
                    {activeDropdown === item.name && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-48 bg-[#0B1014] border border-[#DDA247]/20 shadow-xl" data-id="element-486">
                        {item.dropdown.map(subItem => subItem.isRoute ? <Link key={subItem.name} to={subItem.href} className={`block px-4 py-3 text-sm transition-colors ${location.pathname === subItem.href ? 'text-[#DDA247] bg-white/5' : 'text-gray-300 hover:text-[#DDA247] hover:bg-white/5'}`} data-id="element-487">
                              {subItem.name}
                            </Link> : <a key={subItem.name} href={subItem.href} target="_blank" rel="noopener noreferrer" className="block px-4 py-3 text-sm text-gray-300 hover:text-[#DDA247] hover:bg-white/5 transition-colors" data-id="element-488">
                              {subItem.name}
                            </a>)}
                      </motion.div>}
                  </AnimatePresence>}
              </div>)}
          </nav>

          {/* Actions */}
          <div className="hidden lg:flex items-center space-x-6" data-id="element-489">
            <Link to="/cart" className="text-white hover:text-[#DDA247] transition-colors relative" data-id="element-490">
              <ShoppingCart className="w-5 h-5" data-id="element-491" />
              {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-[#DDA247] text-[#0B1014] text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-id="element-492">
                  {cartCount}
                </span>}
            </Link>
            <Link to="/book-now" data-id="element-493">
              <Button variant="primary" size="sm" data-id="element-494">
                BOOK NOW
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden text-white z-[70] relative p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-495">
            {isMobileMenuOpen ? <X className="w-8 h-8" data-id="element-496" /> : <Menu className="w-8 h-8" data-id="element-497" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay - Outside header for proper fullscreen */}
      <AnimatePresence data-id="element-498">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-0 bg-[#0B1014] z-[60] flex flex-col justify-center items-center lg:hidden overflow-y-auto py-20" data-id="element-499">
            {/* Close button inside overlay */}
            <button className="absolute top-6 right-6 text-white p-2 z-10" onClick={() => setIsMobileMenuOpen(false)} data-id="element-500">
              <X className="w-8 h-8" data-id="element-501" />
            </button>

            <nav className="flex flex-col items-center space-y-4 px-6 w-full" data-id="element-502">
              {navItems.map(item => <div key={item.name} className="flex flex-col items-center w-full space-y-2" data-id="element-503">
                  {item.dropdown ? <button onClick={() => setMobileActiveDropdown(mobileActiveDropdown === item.name ? null : item.name)} className="flex items-center gap-2 text-xl font-medium text-white hover:text-[#DDA247] tracking-widest transition-colors" data-id="element-504">
                      {item.name}
                      <motion.div animate={{
                rotate: mobileActiveDropdown === item.name ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-505">
                        <ChevronDown className="w-5 h-5" data-id="element-506" />
                      </motion.div>
                    </button> : item.isRoute ? <Link to={item.href} className="text-xl font-medium text-white hover:text-[#DDA247] tracking-widest transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-507">
                      {item.name}
                    </Link> : <a href={item.href} className="text-xl font-medium text-white hover:text-[#DDA247] tracking-widest transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-508">
                      {item.name}
                    </a>}

                  {/* Mobile Dropdown Items */}
                  {item.dropdown && <AnimatePresence data-id="element-509">
                      {mobileActiveDropdown === item.name && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden flex flex-col items-center space-y-2 mt-2 w-full" data-id="element-510">
                          {item.dropdown.map(subItem => subItem.isRoute ? <Link key={subItem.name} to={subItem.href} className="text-base font-normal text-gray-400 hover:text-[#DDA247] tracking-wide transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-511">
                                {subItem.name}
                              </Link> : <a key={subItem.name} href={subItem.href} target="_blank" rel="noopener noreferrer" className="text-base font-normal text-gray-400 hover:text-[#DDA247] tracking-wide transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-512">
                                {subItem.name}
                              </a>)}
                        </motion.div>}
                    </AnimatePresence>}
                </div>)}
              <div className="flex items-center justify-center w-full mt-4 mb-2" data-id="element-513">
                <Link to="/cart" className="flex items-center gap-2 text-white hover:text-[#DDA247] transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-514">
                  <div className="relative" data-id="element-515">
                    <ShoppingCart className="w-6 h-6" data-id="element-516" />
                    {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-[#DDA247] text-[#0B1014] text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-id="element-517">
                        {cartCount}
                      </span>}
                  </div>
                  <span className="text-lg font-medium tracking-widest uppercase" data-id="element-518">
                    Cart
                  </span>
                </Link>
              </div>
              <Link to="/book-now" className="w-full max-w-xs" data-id="element-519">
                <Button className="mt-4 w-full" onClick={() => setIsMobileMenuOpen(false)} data-id="element-520">
                  BOOK NOW
                </Button>
              </Link>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </>;
}